import { LoadingButton } from "@mui/lab";
import { Tooltip } from "@mui/material";
import { PlanId } from "components/common/boundary/PlanId";
import { DisableHotkeysInChildren } from "contexts/hotkeys";
import { JSX, ReactElement, useState } from "react";
import { SearchType } from "src/store/api/generatedApi";

/**
 * Configuration for a generic button which can pre-fetch
 * data it depends on.
 */
export interface PreFetchButtonConfig {
  label: string;
  icon: ReactElement;
  disabledTooltip?: string;
  disabled: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  modalComponent: (props: {
    open: boolean;
    handleClose: () => void;
    planId: PlanId;
    searchType: SearchType | null;
  }) => JSX.Element | null;
  searchType: SearchType | null;
  onBeforeOpen?: () => void;
}

interface Props {
  planId: PlanId;
  config: PreFetchButtonConfig;
}

export const PreFetchButton = ({ planId, config }: Props) => {
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    if (config.isLoading) {
      return;
    }

    if (config.onBeforeOpen) {
      config.onBeforeOpen();
    }

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const ModalComponent = config.modalComponent;

  return (
    <>
      {config.disabled && config.disabledTooltip && !config.isLoading ? (
        <Tooltip title={config.disabledTooltip}>
          <span>
            <LoadingButton variant="contained" startIcon={config.icon} disabled>
              {config.label}
            </LoadingButton>
          </span>
        </Tooltip>
      ) : (
        <LoadingButton
          variant="contained"
          startIcon={config.icon}
          onClick={handleClick}
          loading={config.isLoading}
          loadingPosition="end"
          disabled={config.disabled ? !config.isLoading : undefined}
        >
          {config.label}
        </LoadingButton>
      )}

      {open && config.isSuccess ? (
        <DisableHotkeysInChildren>
          <ModalComponent
            open={open}
            handleClose={handleClose}
            planId={planId}
            searchType={config.searchType}
          />
        </DisableHotkeysInChildren>
      ) : null}
    </>
  );
};
